import { Component, OnInit } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ELIGIBILITY_FORM_VALIDATORS } from '@app/core/components/app-container/app-container.component';
import { AppConstants } from '@app/core/constants/app.constants';
import { PatientRecord } from '@app/core/models/patients.model';
import { Payer } from '@app/core/models/payer.model';
import { ProcedureCode } from '@app/core/models/procedure-code.model';
import { Provider } from '@app/core/models/provider.model';
import { OrderByEnum, ProviderSortColumnEnum } from '@app/core/models/requests';
import { GetAppointmentByIdResponse } from '@app/core/models/responses';
import {
  PatientDetailsPageAccess,
  GroupEmbedPageAccess,
  PayerCredentialPageAccess
} from '@app/core/models/route.model';
import { AppService } from '@app/core/services/app/app.service';
import { AppointmentService } from '@app/core/services/appointment/appointment.service';
import { EmbedAuthService } from '@app/core/services/auth/embed-auth.service';
import {
  EligbilityFormType,
  OpenEligbilityEventPayload
} from '@app/core/services/eligibility/eligibility.service';
import { SharedService } from '@app/core/services/shared/shared.service';
import { DateTimeUtils } from '@app/shared/utils/date-time.utils';
import { DxcFormValidators } from '@app/shared/utils/form-validators';
import { Logger } from '@app/shared/utils/logger.utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { environment } from 'projects/portal-ui/src/environments/environment';
import { Subscription } from 'rxjs';
@UntilDestroy()
@Component({
  selector: 'app-check-eligibility',
  templateUrl: './check-eligibility.component.html',
  styleUrls: ['./check-eligibility.component.scss']
})
export class CheckEligibilityComponent implements OnInit {
  now = new Date();
  isEmbedded = false;
  allowedPages!: PatientDetailsPageAccess & GroupEmbedPageAccess & PayerCredentialPageAccess;
  eligibilityForm = this.fb.group({
    formType: this.fb.control<string>(EligbilityFormType.NEW),
    appointmentId: -1,
    firstName: ['', ELIGIBILITY_FORM_VALIDATORS.firstName],
    lastName: ['', ELIGIBILITY_FORM_VALIDATORS.lastName],
    dateOfBirth: this.fb.control<Date>(null!, {
      validators: ELIGIBILITY_FORM_VALIDATORS.dateOfBirth
    }),
    zipCode: ['', ELIGIBILITY_FORM_VALIDATORS.zipCode],
    memberId: ['', ELIGIBILITY_FORM_VALIDATORS.memberId],
    groupId: ['', ELIGIBILITY_FORM_VALIDATORS.groupNumber],
    appointmentDate: this.fb.control<Date>(new Date(), {
      validators: ELIGIBILITY_FORM_VALIDATORS.appointmentDate
    }),
    appointmentTime: [
      { hour: this.now.getHours(), minute: this.now.getMinutes() },
      ELIGIBILITY_FORM_VALIDATORS.appointmentTime
    ],
    provideUIType: ['SELECT'],
    provider: [-1, ELIGIBILITY_FORM_VALIDATORS.provider],
    providerFirstName: ['', ELIGIBILITY_FORM_VALIDATORS.providerName],
    providerLastName: ['', ELIGIBILITY_FORM_VALIDATORS.providerName],
    npi: ['', ELIGIBILITY_FORM_VALIDATORS.providerNpi],
    taxId: ['', ELIGIBILITY_FORM_VALIDATORS.providerTaxId],
    insurancePayer: [-1, ELIGIBILITY_FORM_VALIDATORS.insurancePayer],
    procedureCodes: this.fb.control<string[]>([], {
      validators: ELIGIBILITY_FORM_VALIDATORS.procedureCodes
    }),
    subscriberFirstName: ['', ELIGIBILITY_FORM_VALIDATORS.firstName],
    subscriberLastName: ['', ELIGIBILITY_FORM_VALIDATORS.lastName],
    subscriberDateOfBirth: this.fb.control<Date>(null!, {
      validators: ELIGIBILITY_FORM_VALIDATORS.dateOfBirth
    }),
    subscriberZipCode: ['', ELIGIBILITY_FORM_VALIDATORS.zipCode],
    subscriberMemberId: ['', ELIGIBILITY_FORM_VALIDATORS.memberId],
    subscriberGroupId: ['', ELIGIBILITY_FORM_VALIDATORS.groupNumber],
    relationshipToSubscriber: [
      AppConstants.subscriberRelationShip.Others,
      ELIGIBILITY_FORM_VALIDATORS.relationshipToSubscriber
    ],
    tags: this.fb.control<string[]>([], {
      validators: ELIGIBILITY_FORM_VALIDATORS.tags
    })
  });
  eligibityFormSubscription = Subscription.EMPTY;
  eligibityCheckStep = 1;
  eligibilityModalVisible = false;
  submitLoading = false;
  providers: Provider[] = [];
  payers: Payer[] = [];
  procedurescode: ProcedureCode[] = [];
  tags: string[] = [];
  constructor(
    private fb: NonNullableFormBuilder,
    readonly router: Router,
    private appService: AppService,
    private appointmentService: AppointmentService,
    private sharedService: SharedService,
    private embedAuthService: EmbedAuthService
  ) {
    this.isEmbedded = this.embedAuthService.isEmbedded();
    this.allowedPages = this.embedAuthService.getAllowedPages();
  }

  ngOnInit(): void {
    this.handleEligibilityFormValidation();
    // Keep providers list in sync with global updates

    this.getAllPayers();
    this.getAllProviders();
    this.sharedService.providers$
      .pipe(untilDestroyed(this))
      .subscribe((providers) => (this.providers = providers));
    this.sharedService.tag$
      .pipe(untilDestroyed(this))
      .subscribe((tags) => (this.tags = tags.map((tag) => tag.tag)));
    this.sharedService.procedureCodes$
      .pipe(untilDestroyed(this))
      .subscribe((procedureCodes) => (this.procedurescode = procedureCodes));

    if (this.isEmbedded) {
      this.sharedService.getAllProceduresCode().subscribe({
        next: (response) => {
          if (response.ok && response.body) {
            this.procedurescode = response.body.data;
          }
        }
      });
    }
  }

  onEligibilityModalClose() {
    this.eligibilityModalVisible = false;
    this.eligibityCheckStep = 1;
  }

  rediectToDailyHuddle() {
    this.eligibilityModalVisible = false;
    if (this.isEmbedded) {
      if (this.allowedPages.dailyHuddle)
        setTimeout(() => {
          this.router.navigate(['/e/embed/daily-huddle']);
        });
      return;
    }
    setTimeout(() => {
      this.router.navigate(['/eligibility/daily-huddle']);
    });
  }

  showEligCheckHelpModal() {
    this.appService.lightHouseWidgetNavigate(
      environment.lightHouseWidget.helpDocs.newEligbility.page,
      environment.lightHouseWidget.helpDocs.newEligbility.pageId
    );
  }

  getAllPayers() {
    this.sharedService.getAllPayers().subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.payers = response.body.data;
        }
      }
    });
  }

  getAllProviders() {
    this.sharedService.getProvidersList().subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.providers = response.body.data.results;
        }
      }
    });
  }

  onEligibilitySubscriberSameAsPatient() {
    const { firstName, lastName, groupId, memberId, zipCode, dateOfBirth } =
      this.eligibilityForm.getRawValue();

    this.eligibilityForm.patchValue({
      subscriberFirstName: firstName,
      subscriberLastName: lastName,
      subscriberDateOfBirth: dateOfBirth,
      subscriberGroupId: groupId,
      subscriberMemberId: memberId,
      subscriberZipCode: zipCode,
      relationshipToSubscriber: AppConstants.subscriberRelationShip.Self
    });
  }

  setProviderDetails(id: number) {
    const providers = this.providers.find((x) => x.id == id);
    this.eligibilityForm.patchValue({
      providerFirstName: providers?.firstName,
      providerLastName: providers?.lastName,
      npi: providers?.npi,
      taxId: providers?.tin
    });
  }

  onClearEligibilityForm() {
    if (this.eligibilityForm.value['formType'] !== EligbilityFormType.NEW) return;
    this.eligibilityForm.reset();
    this.eligibilityForm.markAsPristine();
    this.eligibilityForm.markAsUntouched();
    this.eligibilityForm.patchValue({
      appointmentDate: null!,
      appointmentTime: null!
    });
    // Clear hour input via DOM to avoid UI defaulting to 12 when AM/PM is clicked after clear.
    const hourInput = document.querySelector(
      'input[aria-label="Hours"]'
    ) as HTMLInputElement | null;
    if (hourInput) hourInput.value = '';
  }

  onEligibilityFormSubmit() {
    if (this.eligibilityForm.valid) {
      this.submitLoading = true;

      const formData = this.eligibilityForm.getRawValue();
      const isEdit = formData.formType === 'EDIT';

      const appointmentAt = new Date(formData.appointmentDate);
      const appointmentTime = formData.appointmentTime;
      appointmentAt.setHours(appointmentTime.hour, appointmentTime.minute);

      const providerId = +formData.provider;

      const reqBody = {
        patient: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          dateOfBirth: DateTimeUtils.toDateFormat(formData.dateOfBirth),
          address: {
            zipCode: formData.zipCode.replace('-', '')
          }
        },
        providerId: providerId ?? undefined,
        appointmentAt: DateTimeUtils.toISOString(appointmentAt),
        coverage: {
          payerId: +formData.insurancePayer,
          subscriber: {
            firstName: formData.subscriberFirstName,
            lastName: formData.subscriberLastName,
            dateOfBirth: DateTimeUtils.toDateFormat(formData.subscriberDateOfBirth),
            memberId: formData.subscriberMemberId,
            groupNumber: formData.subscriberGroupId ? formData.subscriberGroupId : null,
            address: {
              zipCode: formData.subscriberZipCode.replace('-', '')
            }
          },
          patientCoverage: {
            memberId: formData.memberId,
            groupNumber: formData.groupId ? formData.groupId : null,
            relationshipToSubscriber: formData.relationshipToSubscriber
          }
        },
        procedureCodes: formData.procedureCodes,
        provider: providerId
          ? undefined
          : {
              firstName: formData.providerFirstName,
              lastName: formData.providerLastName,
              npi: formData.npi,
              taxId: formData.taxId
            },
        tags: formData.tags
      };

      const request = isEdit
        ? this.appointmentService.updateAppointment(formData.appointmentId, reqBody)
        : this.appointmentService.createAppointment(reqBody);

      request.subscribe({
        next: (response) => {
          if (response.body && response.body.success) {
            if (isEdit) this.eligibilityModalVisible = false;
            else this.eligibityCheckStep = 2;
            this.eligibilityForm.reset();
            this.sharedService.getTagsListForce().subscribe((response) => {
              if (response.ok && response.body) {
                this.tags = response.body.data.map((tag) => {
                  return tag.tag;
                });
              }
            });
          }
          this.submitLoading = false;
        },
        error: () => {
          this.submitLoading = false;
        }
      });
    }
  }

  onNewEligibilityCheck() {
    const now = new Date();

    this.eligibilityForm = this.fb.group({
      formType: EligbilityFormType.NEW,
      appointmentId: -1,
      firstName: ['', ELIGIBILITY_FORM_VALIDATORS.firstName],
      lastName: ['', ELIGIBILITY_FORM_VALIDATORS.lastName],
      dateOfBirth: this.fb.control<Date>(null!, {
        validators: ELIGIBILITY_FORM_VALIDATORS.dateOfBirth
      }),
      zipCode: ['', ELIGIBILITY_FORM_VALIDATORS.zipCode],
      memberId: ['', ELIGIBILITY_FORM_VALIDATORS.memberId],
      groupId: ['', ELIGIBILITY_FORM_VALIDATORS.groupNumber],
      appointmentDate: this.fb.control<Date>(new Date(), {
        validators: ELIGIBILITY_FORM_VALIDATORS.appointmentDate
      }),
      appointmentTime: [
        { hour: now.getHours(), minute: now.getMinutes() },
        ELIGIBILITY_FORM_VALIDATORS.appointmentTime
      ],
      provideUIType: ['SELECT'],
      provider: this.fb.control<number>(null!, {
        validators: ELIGIBILITY_FORM_VALIDATORS.provider
      }),
      providerFirstName: ['', ELIGIBILITY_FORM_VALIDATORS.providerName],
      providerLastName: ['', ELIGIBILITY_FORM_VALIDATORS.providerName],
      npi: ['', ELIGIBILITY_FORM_VALIDATORS.providerNpi],
      taxId: ['', ELIGIBILITY_FORM_VALIDATORS.providerTaxId],
      insurancePayer: this.fb.control<number>(null!, {
        validators: ELIGIBILITY_FORM_VALIDATORS.insurancePayer
      }),
      procedureCodes: this.fb.control<string[]>([], {
        validators: ELIGIBILITY_FORM_VALIDATORS.procedureCodes
      }),
      subscriberFirstName: ['', ELIGIBILITY_FORM_VALIDATORS.firstName],
      subscriberLastName: ['', ELIGIBILITY_FORM_VALIDATORS.lastName],
      subscriberDateOfBirth: this.fb.control<Date>(null!, {
        validators: ELIGIBILITY_FORM_VALIDATORS.dateOfBirth
      }),
      subscriberZipCode: ['', ELIGIBILITY_FORM_VALIDATORS.zipCode],
      subscriberMemberId: ['', ELIGIBILITY_FORM_VALIDATORS.memberId],
      subscriberGroupId: ['', ELIGIBILITY_FORM_VALIDATORS.groupNumber],
      relationshipToSubscriber: [
        AppConstants.subscriberRelationShip.Others,
        ELIGIBILITY_FORM_VALIDATORS.relationshipToSubscriber
      ],
      tags: this.fb.control<string[]>([], {
        validators: ELIGIBILITY_FORM_VALIDATORS.tags
      })
    });

    if (!this.isEmbedded || this.allowedPages.dailyHuddle) this.eligibilityModalVisible = true;

    this.handleEligibilityFormValidation();
  }

  onEditAppointment(appointment: GetAppointmentByIdResponse) {
    const appointmentAt = new Date(appointment.appointmentAt);

    this.eligibilityForm = this.fb.group({
      formType: EligbilityFormType.EDIT,
      appointmentId: appointment.appointmentId,
      firstName: [appointment.patient.firstName, ELIGIBILITY_FORM_VALIDATORS.firstName],
      lastName: [appointment.patient.lastName, ELIGIBILITY_FORM_VALIDATORS.lastName],
      dateOfBirth: [
        DateTimeUtils.isoDateToLocalDate(appointment?.patient?.dob),
        ELIGIBILITY_FORM_VALIDATORS.dateOfBirth
      ],
      zipCode: [appointment.patient.zipCode ?? '', ELIGIBILITY_FORM_VALIDATORS.zipCode],
      memberId: [appointment.patient.memberId ?? '', ELIGIBILITY_FORM_VALIDATORS.memberId],
      groupId: [appointment.patient.groupNumber ?? '', ELIGIBILITY_FORM_VALIDATORS.groupNumber],
      appointmentDate: [
        new Date(appointment.appointmentAt),
        ELIGIBILITY_FORM_VALIDATORS.appointmentDate
      ],
      appointmentTime: [
        { hour: appointmentAt.getHours(), minute: appointmentAt.getMinutes() },
        ELIGIBILITY_FORM_VALIDATORS.appointmentTime
      ],
      provideUIType: ['SELECT'],
      provider: [appointment.provider?.id ?? -1, ELIGIBILITY_FORM_VALIDATORS.provider],
      providerFirstName: ['', ELIGIBILITY_FORM_VALIDATORS.providerName],
      providerLastName: ['', ELIGIBILITY_FORM_VALIDATORS.providerName],
      npi: ['', ELIGIBILITY_FORM_VALIDATORS.providerNpi],
      taxId: ['', ELIGIBILITY_FORM_VALIDATORS.providerTaxId],
      insurancePayer: [
        appointment.insurance?.payerId ?? -1,
        ELIGIBILITY_FORM_VALIDATORS.insurancePayer
      ],
      procedureCodes: [appointment.procedureCodes, ELIGIBILITY_FORM_VALIDATORS.procedureCodes],
      subscriberFirstName: [
        appointment.subscriber?.firstName ?? '',
        ELIGIBILITY_FORM_VALIDATORS.firstName
      ],
      subscriberLastName: [
        appointment.subscriber?.lastName ?? '',
        ELIGIBILITY_FORM_VALIDATORS.lastName
      ],
      subscriberDateOfBirth: [
        DateTimeUtils.isoDateToLocalDate(appointment.subscriber?.dob),
        ELIGIBILITY_FORM_VALIDATORS.dateOfBirth
      ],
      subscriberZipCode: [
        appointment.subscriber?.zipCode ?? '',
        ELIGIBILITY_FORM_VALIDATORS.zipCode
      ],
      subscriberMemberId: [
        appointment.subscriber?.memberId ?? '',
        ELIGIBILITY_FORM_VALIDATORS.memberId
      ],
      subscriberGroupId: [
        appointment.subscriber?.groupNumber ?? '',
        ELIGIBILITY_FORM_VALIDATORS.groupNumber
      ],
      relationshipToSubscriber: [
        appointment.patient?.relationshipToSubscriber as string,
        ELIGIBILITY_FORM_VALIDATORS.relationshipToSubscriber
      ],
      tags: [appointment.tags, ELIGIBILITY_FORM_VALIDATORS.tags]
    });
    this.handleEligibilityFormValidation();
    this.eligibilityModalVisible = true;
  }

  onAddAppointment(patient: PatientRecord) {
    const now = new Date();

    this.eligibilityForm = this.fb.group({
      formType: EligbilityFormType.ADD_APPOINTMENT,
      appointmentId: -1,
      firstName: [
        {
          value: patient.firstName,
          disabled: true
        },
        ELIGIBILITY_FORM_VALIDATORS.firstName
      ],
      lastName: [
        {
          value: patient.lastName,
          disabled: true
        },
        ELIGIBILITY_FORM_VALIDATORS.lastName
      ],
      dateOfBirth: [
        {
          value: DateTimeUtils.isoDateToLocalDate(patient.dob),
          disabled: true
        },
        ELIGIBILITY_FORM_VALIDATORS.dateOfBirth
      ],
      zipCode: [patient.zipCode ?? '', ELIGIBILITY_FORM_VALIDATORS.zipCode],
      memberId: [patient.memberId ?? '', ELIGIBILITY_FORM_VALIDATORS.memberId],
      groupId: [patient.groupNumber ?? '', ELIGIBILITY_FORM_VALIDATORS.groupNumber],
      appointmentDate: [new Date(), ELIGIBILITY_FORM_VALIDATORS.appointmentDate],
      appointmentTime: [
        { hour: now.getHours(), minute: now.getMinutes() },
        ELIGIBILITY_FORM_VALIDATORS.appointmentTime
      ],
      provideUIType: ['SELECT'],
      provider: [patient.provider?.id ?? -1, ELIGIBILITY_FORM_VALIDATORS.provider],
      providerFirstName: [
        patient.provider?.firstName ?? '',
        ELIGIBILITY_FORM_VALIDATORS.providerName
      ],
      providerLastName: [
        patient.provider?.lastName ?? '',
        ELIGIBILITY_FORM_VALIDATORS.providerName
      ],
      npi: [patient.provider?.npi ?? '', ELIGIBILITY_FORM_VALIDATORS.providerNpi],
      taxId: [patient.provider?.tin ?? '', ELIGIBILITY_FORM_VALIDATORS.providerTaxId],
      insurancePayer: [patient.payer?.id ?? -1, ELIGIBILITY_FORM_VALIDATORS.insurancePayer],
      procedureCodes: this.fb.control<string[]>([], {
        validators: ELIGIBILITY_FORM_VALIDATORS.procedureCodes
      }),
      subscriberFirstName: [
        patient.subscriber?.firstName ?? '',
        ELIGIBILITY_FORM_VALIDATORS.firstName
      ],
      subscriberLastName: [
        patient.subscriber?.lastName ?? '',
        ELIGIBILITY_FORM_VALIDATORS.lastName
      ],
      subscriberDateOfBirth: this.fb.control<Date>(
        DateTimeUtils.isoDateToLocalDate(patient?.subscriber?.dob),
        {
          validators: ELIGIBILITY_FORM_VALIDATORS.dateOfBirth
        }
      ),
      subscriberZipCode: [patient.subscriber?.zipCode, ELIGIBILITY_FORM_VALIDATORS.zipCode],
      subscriberMemberId: [patient.subscriber?.memberId, ELIGIBILITY_FORM_VALIDATORS.memberId],
      subscriberGroupId: [patient.subscriber?.groupNumber, ELIGIBILITY_FORM_VALIDATORS.groupNumber],
      relationshipToSubscriber: [patient?.relationshipToSubscriber, Validators.required],
      tags: this.fb.control<string[]>([], {
        validators: ELIGIBILITY_FORM_VALIDATORS.tags
      })
    });

    this.handleEligibilityFormValidation();
    this.eligibilityModalVisible = true;
  }

  handleOpenEligibilityFormEvent(event: OpenEligbilityEventPayload) {
    Logger.trace('handleOpenEligibilityFormEvent: ', event);
    switch (event.type) {
      case 'NEW': {
        this.onNewEligibilityCheck();
        break;
      }

      case 'ADD_APPOINTMENT': {
        this.onAddAppointment(event.data);
        break;
      }

      case 'EDIT': {
        this.onEditAppointment(event.data);
        break;
      }
    }
  }

  handleEligibilityFormValidation() {
    this.eligibityFormSubscription.unsubscribe();

    this.eligibityFormSubscription = this.eligibilityForm.controls[
      'provideUIType'
    ].valueChanges.subscribe((data) => {
      Logger.trace('provideUIType: ', data);
      if (data === 'ENTER') {
        this.eligibilityForm.controls['providerFirstName'].addValidators([Validators.required]);
        this.eligibilityForm.controls['providerLastName'].addValidators([Validators.required]);
        this.eligibilityForm.controls['npi'].addValidators([
          Validators.required,
          DxcFormValidators.npi
        ]);
        this.eligibilityForm.controls['taxId'].addValidators([
          Validators.required,
          DxcFormValidators.taxId
        ]);
        this.eligibilityForm.controls['provider'].clearValidators();
        this.eligibilityForm.controls['provider'].setValue(null!);
      } else {
        this.eligibilityForm.controls['provider'].addValidators([Validators.required]);
        [
          this.eligibilityForm.controls['providerFirstName'],
          this.eligibilityForm.controls['providerLastName'],
          this.eligibilityForm.controls['npi'],
          this.eligibilityForm.controls['taxId']
        ].forEach((e) => {
          e.clearValidators();
          e.setErrors(null);
        });
      }
    });
  }
}
